import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import CipherHeader from './components/ui/CipherHeader';
import { MOVEMENT_CATEGORIES, SECONDARY_MUSCLES, ALL_SECONDARY_MUSCLES, MULTIPLIER_PRESETS } from './constants/exerciseCategories';
import { toastSuccess, toastError } from './ui/toast';
import ConfirmDialog from './ui/ConfirmDialog';
import MuscleSelector from './components/MuscleSelector';
import { useUserRole } from './hooks/useUserRole';
import ExerciseDemo from './components/ui/ExerciseDemo';
import { colors as theme } from './theme';

const ExerciseLibraryManager = ({ onBack }) => {
  const { isSuperAdmin } = useUserRole();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMuscle, setFilterMuscle] = useState('All');
  const [filterSecondaryMuscle, setFilterSecondaryMuscle] = useState('All');
  const [showSimilarFor, setShowSimilarFor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, exerciseId: null });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    movement_category: '',
    secondary_muscles: '',
    equipment: '',
    difficulty_level: 'Beginner',
    exercise_type: 'resistance',
    duration_unit: 'minutes',
    video_url: '',
    gif_url: '',
    is_bodyweight: false,
    is_assisted: false,
    is_unilateral: false,
    bodyweight_multiplier: 1.0
  });

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('exercises')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setExercises(data || []);
    } catch (err) {
      console.error('ExerciseLibraryManager: Error loading exercises:', err);
      toastError('Error loading exercises. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    // Prepare data for saving - only include fields that exist in the database
    const saveData = {
      name: formData.name,
      movement_category: formData.movement_category,
      secondary_muscles: formData.secondary_muscles,
      equipment: formData.equipment,
      difficulty_level: formData.difficulty_level,
      video_url: formData.video_url || null,
      gif_url: formData.gif_url || null,
      exercise_type: formData.exercise_type || 'resistance',
      duration_unit: (formData.exercise_type === 'cardio' || formData.exercise_type === 'stretch' || formData.exercise_type === 'isometric') ? (formData.duration_unit || 'minutes') : 'minutes',
      is_bodyweight: formData.is_bodyweight || false,
      is_assisted: formData.is_assisted || false,
      is_unilateral: formData.is_unilateral || false,
      bodyweight_multiplier: formData.is_bodyweight ? (formData.bodyweight_multiplier || 1.0) : 1.0
    };

    try {
      let savedExerciseId = null;

      if (editingExercise) {
        // Update existing exercise
        const { error } = await supabase
          .from('exercises')
          .update(saveData)
          .eq('id', editingExercise.id);

        if (error) throw error;
        toastSuccess('Exercise updated successfully!');
        savedExerciseId = editingExercise.id; // Remember which exercise we edited
      } else {
        // Create new exercise
        const { error } = await supabase
          .from('exercises')
          .insert([saveData]);

        if (error) throw error;
        toastSuccess('Exercise added successfully!');
      }

      // Reset form and reload
      setFormData({
        name: '',
        movement_category: '',
        secondary_muscles: '',
        equipment: '',
        difficulty_level: 'Beginner',
        video_url: '',
        gif_url: '',
        is_bodyweight: false,
        is_assisted: false,
        is_unilateral: false,
        bodyweight_multiplier: 1.0
      });
      setShowForm(false);
      setEditingExercise(null);
      setHasUnsavedChanges(false);
      await loadExercises(); // Wait for reload to complete

      // Scroll back to the saved exercise after reload and highlight it
      if (savedExerciseId) {
        setTimeout(() => {
          const exerciseElement = document.getElementById(`exercise-${savedExerciseId}`);
          if (exerciseElement) {
            exerciseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Add a brief highlight effect
            const originalBorder = exerciseElement.style.border;
            exerciseElement.style.border = `2px solid ${theme.gold}`;
            exerciseElement.style.transition = 'border 0.3s ease';

            setTimeout(() => {
              exerciseElement.style.border = originalBorder;
            }, 2000);
          }
        }, 200);
      }
    } catch (err) {
      console.error('ExerciseLibraryManager: Error saving exercise:', err);
      toastError(`Error saving exercise: ${err.message}`);
      // Don't close form on error - stay in edit mode
    }
  };

  // Get similar exercises based on movement category and secondary muscles
  const getSimilarExercises = (exercise) => {
    return exercises.filter(ex => {
      if (ex.id === exercise.id) return false;

      // Match by movement category
      if (ex.movement_category === exercise.movement_category) return true;

      // Match by secondary muscle overlap
      if (exercise.secondary_muscles && ex.secondary_muscles) {
        const exerciseSecondary = exercise.secondary_muscles.split(', ');
        const exSecondary = ex.secondary_muscles.split(', ');
        return exerciseSecondary.some(m => exSecondary.includes(m));
      }

      return false;
    });
  };

  // Filter exercises based on search, movement category, and secondary muscle
  const getFilteredExercises = () => {
    return exercises.filter(ex => {
      const matchesSearch = ex.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterMuscle === 'All' || ex.movement_category === filterMuscle;
      const matchesSecondary = filterSecondaryMuscle === 'All' ||
        (ex.secondary_muscles && ex.secondary_muscles.includes(filterSecondaryMuscle));
      return matchesSearch && matchesCategory && matchesSecondary;
    });
  };

  // Get unique movement categories from exercises
  const getUniqueMovementCategories = () => {
    const categories = new Set(exercises.map(e => e.movement_category).filter(Boolean));
    return ['All', ...Array.from(categories).sort()];
  };

  // Get available secondary muscles for filtering based on current category filter
  const getFilterSecondaryMuscles = () => {
    if (filterMuscle === 'All') {
      return ['All', ...ALL_SECONDARY_MUSCLES];
    }
    return ['All', ...(SECONDARY_MUSCLES[filterMuscle] || [])];
  };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setFormData({
      name: exercise.name,
      movement_category: exercise.movement_category || '',
      secondary_muscles: exercise.secondary_muscles || '',
      equipment: exercise.equipment || '',
      difficulty_level: exercise.difficulty_level || 'Beginner',
      exercise_type: exercise.exercise_type || 'resistance',
      duration_unit: exercise.duration_unit || 'minutes',
      video_url: exercise.video_url || '',
      gif_url: exercise.gif_url || '',
      is_bodyweight: exercise.is_bodyweight || false,
      is_assisted: exercise.is_assisted || false,
      is_unilateral: exercise.is_unilateral || false,
      bodyweight_multiplier: exercise.bodyweight_multiplier || 1.0
    });
    setHasUnsavedChanges(false); // Reset when starting to edit
    setShowForm(true);

    // Scroll to the form smoothly after it appears
    setTimeout(() => {
      const formElement = document.getElementById(`exercise-form-${exercise.id}`);
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // Get similar exercises that have muscles defined
  const getSimilarExercisesWithMuscles = () => {
    if (!formData.movement_category) return [];
    return exercises
      .filter(ex =>
        ex.movement_category === formData.movement_category &&
        ex.secondary_muscles &&
        ex.secondary_muscles.trim().length > 0 &&
        ex.id !== editingExercise?.id
      )
      .slice(0, 5);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, exerciseId: id });
  };

  const handleConfirmDelete = async () => {
    const { exerciseId } = confirmDialog;
    try {
      const { error } = await supabase
        .from('exercises')
        .delete()
        .eq('id', exerciseId);

      if (error) throw error;
      toastSuccess('Exercise deleted successfully!');
      setConfirmDialog({ isOpen: false, exerciseId: null });
      await loadExercises();
    } catch (err) {
      console.error('ExerciseLibraryManager: Error deleting exercise:', err);
      toastError('Error deleting exercise. Please try again.');
      setConfirmDialog({ isOpen: false, exerciseId: null });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingExercise(null);
    setHasUnsavedChanges(false);
    setFormData({
      name: '',
      movement_category: '',
      secondary_muscles: '',
      equipment: '',
      difficulty_level: 'Beginner',
      video_url: '',
      gif_url: '',
      is_bodyweight: false,
      is_assisted: false,
      is_unilateral: false
    });
  };

  const handleBack = () => {
    if (hasUnsavedChanges && showForm) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      );
      if (!confirmLeave) {
        return;
      }
    }
    onBack();
  };

  // Export exercises as JSON backup
  const handleExportBackup = () => {
    const backup = {
      exportDate: new Date().toISOString(),
      totalExercises: exercises.length,
      exercises: exercises
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exercise-library-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get exercise counts by movement category
  const getMovementCategoryStats = () => {
    const stats = {};
    exercises.forEach(ex => {
      const category = ex.movement_category || 'Uncategorized';
      stats[category] = (stats[category] || 0) + 1;
    });
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: theme.textMuted }}>
        Loading exercises...
      </div>
    );
  }

  return (
    <div style={{
      padding: '40px',
      backgroundColor: theme.bg,
      minHeight: '100vh'
    }}>
      <CipherHeader title="Exercise Library" subtitle={`${exercises.length} exercises in your library`} onBack={handleBack} />

      {/* Action buttons row */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleExportBackup}
              style={{
                backgroundColor: 'transparent',
                border: `2px solid ${theme.success}`,
                color: theme.success,
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 700
              }}
            >
              📥 Backup
            </button>
            {isSuperAdmin && <button
              onClick={() => {
                setEditingExercise(null);
                setFormData({
                  name: '',
                  movement_category: '',
                  secondary_muscles: '',
                  equipment: '',
                  difficulty_level: 'Beginner',
                  video_url: '',
                  gif_url: '',
                  is_bodyweight: false,
                  is_assisted: false,
                  is_unilateral: false
                });
                setHasUnsavedChanges(false); // Reset when starting new
                setShowForm(true);

                // Scroll to the form smoothly
                setTimeout(() => {
                  const formElement = document.getElementById('exercise-form');
                  if (formElement) {
                    formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              style={{
                backgroundColor: theme.gold,
                border: 'none',
                color: theme.bg,
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 700
              }}
            >
              + Add Exercise
            </button>}
          </div>
        </div>

        {/* Exercise Type Guide */}
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${theme.surface3}`
        }}>
          <h3 style={{ color: theme.gold, marginBottom: '15px', fontSize: '16px' }}>
            📋 Exercise Type Guide
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
            <div style={{ backgroundColor: theme.bg, padding: '12px', borderRadius: '8px' }}>
              <div style={{ color: theme.text, fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>
                Regular Exercise
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                Bench press, squats, etc.
                <br />
                <span style={{ color: theme.gold }}>Client logs:</span> Actual weight (100kg)
                <br />
                <span style={{ color: theme.gold }}>Display:</span> 100kg
              </div>
            </div>
            <div style={{ backgroundColor: theme.bg, padding: '12px', borderRadius: '8px', border: `2px solid ${theme.success}` }}>
              <div style={{ color: theme.success, fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>
                💪 Bodyweight Exercise
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                Pull-ups, push-ups, dips
                <br />
                <span style={{ color: theme.gold }}>Client logs:</span> 0kg or +10kg (added)
                <br />
                <span style={{ color: theme.gold }}>Display:</span> BW or BW +10kg
              </div>
            </div>
            <div style={{ backgroundColor: theme.bg, padding: '12px', borderRadius: '8px', border: '2px solid #5AC8FA' }}>
              <div style={{ color: '#5AC8FA', fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>
                🤝 Assisted Exercise
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                Assisted pull-ups, band work
                <br />
                <span style={{ color: theme.gold }}>Client logs:</span> 20kg (assistance)
                <br />
                <span style={{ color: theme.gold }}>Display:</span> Assisted -20kg
              </div>
            </div>
          </div>
        </div>

        {/* Movement Category Stats */}
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${theme.surface3}`
        }}>
          <h3 style={{ color: theme.gold, marginBottom: '15px', fontSize: '16px' }}>
            Exercises by Movement Category
          </h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {getMovementCategoryStats().map(([category, count]) => (
              <div
                key={category}
                onClick={() => {
                  setFilterMuscle(category);
                  setFilterSecondaryMuscle('All');
                }}
                style={{
                  backgroundColor: filterMuscle === category ? theme.gold : theme.bg,
                  padding: '8px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: `1px solid ${theme.surface3}`
                }}
              >
                <span style={{
                  color: filterMuscle === category ? theme.bg : 'white',
                  fontWeight: 700,
                  fontSize: '14px'
                }}>
                  {category}
                </span>
                <span style={{
                  color: filterMuscle === category ? theme.bg : theme.gold,
                  marginLeft: '8px',
                  fontWeight: 700,
                  fontSize: '14px'
                }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filter */}
        <div style={{
          backgroundColor: theme.surface1,
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${theme.surface3}`
        }}>
          <input
            type="text"
            placeholder="Search exercises..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.bg,
              border: `1px solid ${theme.surface3}`,
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px',
              marginBottom: '10px',
              boxSizing: 'border-box'
            }}
          />
          {/* Movement Category Filter */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>
              Movement Category
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {getUniqueMovementCategories().map(category => (
                <button
                  key={category}
                  onClick={() => {
                    setFilterMuscle(category);
                    setFilterSecondaryMuscle('All');
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: filterMuscle === category ? theme.gold : theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '20px',
                    color: filterMuscle === category ? theme.bg : theme.textMuted,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: filterMuscle === category ? 'bold' : 'normal'
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          {/* Secondary Muscle Filter */}
          <div>
            <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>
              Secondary Muscle
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {getFilterSecondaryMuscles().map(muscle => (
                <button
                  key={muscle}
                  onClick={() => setFilterSecondaryMuscle(muscle)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: filterSecondaryMuscle === muscle ? theme.gold : theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '20px',
                    color: filterSecondaryMuscle === muscle ? theme.bg : theme.textMuted,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: filterSecondaryMuscle === muscle ? 'bold' : 'normal'
                  }}
                >
                  {muscle}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form - Only show at top when adding new exercise (not editing) */}
      {showForm && !editingExercise && (
        <div id="exercise-form" style={{
          backgroundColor: theme.surface1,
          padding: '25px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: `1px solid ${theme.surface3}`
        }}>
          <h2 style={{ color: theme.gold, marginBottom: '20px' }}>
            {editingExercise ? 'Edit Exercise' : 'Add New Exercise'}
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: '15px' }}>
              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  Exercise Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  Primary Movement Category *
                </label>
                <select
                  required
                  value={formData.movement_category}
                  onChange={(e) => {
                    setFormData({...formData, movement_category: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text,
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select movement category</option>
                  {MOVEMENT_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '8px', fontSize: '12px' }}>
                  <strong style={{ color: theme.text, fontSize: '13px' }}>Muscles Involved in Exercise</strong>
                  <br />
                  Select all muscles activated during this exercise. Used for body highlighting in daily reports.
                </label>

                {/* Quick Auto-Suggest Buttons */}
                {formData.movement_category && SECONDARY_MUSCLES[formData.movement_category] && (
                  <div style={{
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    borderRadius: '8px',
                    border: `1px solid ${theme.surface3}`
                  }}>
                    <div style={{ color: theme.gold, fontSize: '12px', marginBottom: '6px', fontWeight: 700 }}>
                      💡 Quick Apply for {formData.movement_category}:
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          const suggestedMuscles = SECONDARY_MUSCLES[formData.movement_category].slice(0, 3).join(', ');
                          setFormData({...formData, secondary_muscles: suggestedMuscles});
                          setHasUnsavedChanges(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: theme.success,
                          border: 'none',
                          borderRadius: '8px',
                          color: theme.bg,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 700
                        }}
                      >
                        ✓ Apply Top 3
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          const allMuscles = SECONDARY_MUSCLES[formData.movement_category].join(', ');
                          setFormData({...formData, secondary_muscles: allMuscles});
                          setHasUnsavedChanges(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#5AC8FA',
                          border: 'none',
                          borderRadius: '8px',
                          color: theme.bg,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 700
                        }}
                      >
                        ✓ Apply All
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFormData({...formData, secondary_muscles: ''});
                          setHasUnsavedChanges(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.textMuted}`,
                          borderRadius: '8px',
                          color: theme.textMuted,
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Copy from Similar Exercises */}
                {getSimilarExercisesWithMuscles().length > 0 && (
                  <div style={{
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    borderRadius: '8px',
                    border: `1px solid ${theme.surface3}`
                  }}>
                    <div style={{ color: theme.gold, fontSize: '12px', marginBottom: '6px', fontWeight: 700 }}>
                      📋 Copy from Similar Exercise:
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {getSimilarExercisesWithMuscles().map(ex => (
                        <button
                          key={ex.id}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData({...formData, secondary_muscles: ex.secondary_muscles});
                            setHasUnsavedChanges(true);
                            toastSuccess(`Copied muscles from: ${ex.name}`);
                          }}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.gold}`,
                            borderRadius: '8px',
                            color: theme.gold,
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          {ex.name.substring(0, 20)}{ex.name.length > 20 ? '...' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <MuscleSelector
                  selectedMuscles={formData.secondary_muscles}
                  onChange={(muscles) => {
                    setFormData({...formData, secondary_muscles: muscles});
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  Equipment
                </label>
                <input
                  type="text"
                  value={formData.equipment}
                  onChange={(e) => {
                    setFormData({...formData, equipment: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="e.g., Barbell, Dumbbells, Cable"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  Logging Type
                </label>
                <select
                  value={formData.exercise_type}
                  onChange={(e) => {
                    setFormData({...formData, exercise_type: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text,
                    marginBottom: '15px',
                  }}
                >
                  <option value="resistance">Resistance (weight × reps)</option>
                  <option value="isometric">Isometric (hold in seconds)</option>
                  <option value="cardio">Cardio (duration + intensity)</option>
                  <option value="stretch">Stretch (duration + intensity)</option>
                </select>

                {(formData.exercise_type === 'cardio' || formData.exercise_type === 'stretch' || formData.exercise_type === 'isometric') && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                      Duration Unit
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['minutes', 'seconds'].map(unit => (
                        <button
                          key={unit}
                          type="button"
                          onClick={() => { setFormData({ ...formData, duration_unit: unit }); setHasUnsavedChanges(true); }}
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            border: formData.duration_unit === unit ? `2px solid ${theme.gold}` : `1px solid ${theme.surface3}`,
                            backgroundColor: formData.duration_unit === unit ? 'rgba(212,175,55,0.15)' : theme.bg,
                            color: formData.duration_unit === unit ? theme.gold : theme.textMuted,
                            fontWeight: formData.duration_unit === unit ? '700' : '400',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {unit === 'minutes' ? 'Minutes (long cardio)' : 'Seconds (intervals / drills)'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  Difficulty Level
                </label>
                <select
                  value={formData.difficulty_level}
                  onChange={(e) => {
                    setFormData({...formData, difficulty_level: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text
                  }}
                >
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>

              {/* Exercise Type Toggles */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                padding: '15px',
                backgroundColor: theme.bg,
                borderRadius: '8px',
                border: `1px solid ${theme.surface3}`
              }}>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    color: formData.is_bodyweight ? theme.gold : theme.textMuted,
                    fontWeight: formData.is_bodyweight ? 'bold' : 'normal',
                    fontSize: '14px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_bodyweight}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_bodyweight: e.target.checked,
                          is_assisted: e.target.checked ? false : formData.is_assisted,
                          bodyweight_multiplier: e.target.checked ? formData.bodyweight_multiplier : 1.0
                        });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: theme.gold
                      }}
                    />
                    Bodyweight Exercise
                  </label>
                  <div style={{
                    fontSize: '11px',
                    color: theme.textMuted,
                    marginTop: '5px',
                    marginLeft: '28px'
                  }}>
                    Pull-ups, push-ups, dips, etc.
                    <br />
                    Weight field = added weight
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    color: formData.is_assisted ? theme.gold : theme.textMuted,
                    fontWeight: formData.is_assisted ? 'bold' : 'normal',
                    fontSize: '14px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_assisted}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_assisted: e.target.checked,
                          is_bodyweight: e.target.checked ? false : formData.is_bodyweight
                        });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: theme.gold
                      }}
                    />
                    Assisted Exercise
                  </label>
                  <div style={{
                    fontSize: '11px',
                    color: theme.textMuted,
                    marginTop: '5px',
                    marginLeft: '28px'
                  }}>
                    Assisted pull-ups, band-assisted
                    <br />
                    More weight = easier
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    color: formData.is_unilateral ? theme.gold : theme.textMuted,
                    fontWeight: formData.is_unilateral ? 'bold' : 'normal',
                    fontSize: '14px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_unilateral}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_unilateral: e.target.checked
                        });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: theme.gold
                      }}
                    />
                    Unilateral (one side at a time)
                  </label>
                  <div style={{
                    fontSize: '11px',
                    color: theme.textMuted,
                    marginTop: '5px',
                    marginLeft: '28px'
                  }}>
                    Single Leg Extension, single-arm rows, etc.
                    <br />
                    ProgramBuilder suggests doubled sets; logger labels Left/Right
                  </div>
                </div>
              </div>

              {/* Bodyweight Multiplier - Only shown for bodyweight exercises */}
              {formData.is_bodyweight && (
                <div style={{
                  padding: '15px',
                  backgroundColor: theme.bg,
                  borderRadius: '8px',
                  border: `2px solid ${theme.success}`
                }}>
                  <label style={{ color: theme.success, display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                    ⚖️ Bodyweight Percentage (Multiplier)
                  </label>
                  <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px', lineHeight: '1.5' }}>
                    What percentage of bodyweight is actually moved in this exercise?
                    <br />
                    <strong style={{ color: theme.gold }}>Examples:</strong> Pull-ups = 100% (1.0), Push-ups = 64% (0.64), Sit-ups = 35% (0.35)
                  </div>

                  {/* Quick Preset Buttons */}
                  <div style={{
                    marginBottom: '12px',
                    padding: '10px',
                    backgroundColor: theme.surface1,
                    borderRadius: '8px',
                    border: `1px solid ${theme.surface3}`
                  }}>
                    <div style={{ color: theme.gold, fontSize: '11px', marginBottom: '6px', fontWeight: 700 }}>
                      💡 Quick Presets:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                      {MULTIPLIER_PRESETS.slice(0, 9).map(preset => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData({...formData, bodyweight_multiplier: preset.value});
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: formData.bodyweight_multiplier === preset.value ? theme.success : 'transparent',
                            border: `1px solid ${formData.bodyweight_multiplier === preset.value ? theme.success : theme.surface3}`,
                            borderRadius: '8px',
                            color: formData.bodyweight_multiplier === preset.value ? theme.bg : theme.textMuted,
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: formData.bodyweight_multiplier === preset.value ? 'bold' : 'normal',
                            textAlign: 'left'
                          }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Manual Input */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="number"
                      min="0.1"
                      max="1.0"
                      step="0.01"
                      value={formData.bodyweight_multiplier}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (value >= 0.1 && value <= 1.0) {
                          setFormData({...formData, bodyweight_multiplier: value});
                          setHasUnsavedChanges(true);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: theme.surface1,
                        border: `2px solid ${theme.success}`,
                        borderRadius: '8px',
                        color: theme.text,
                        fontSize: '16px',
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                    />
                    <div style={{ color: theme.text, fontSize: '16px', fontWeight: 700 }}>
                      = {Math.round(formData.bodyweight_multiplier * 100)}%
                    </div>
                  </div>

                  {/* Calculation Example */}
                  <div style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: theme.surface1,
                    borderRadius: '8px',
                    fontSize: '11px',
                    color: theme.textMuted,
                    lineHeight: '1.5'
                  }}>
                    <strong style={{ color: theme.gold }}>Example:</strong> 70kg person doing 10 reps
                    <br />
                    Effective weight per rep: 70kg × {formData.bodyweight_multiplier} = <strong style={{ color: theme.text }}>{(70 * formData.bodyweight_multiplier).toFixed(1)}kg</strong>
                    <br />
                    Total volume: {(70 * formData.bodyweight_multiplier).toFixed(1)}kg × 10 = <strong style={{ color: theme.success }}>{(70 * formData.bodyweight_multiplier * 10).toFixed(0)}kg</strong>
                  </div>
                </div>
              )}

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  YouTube Video URL (your Shorts)
                </label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => {
                    setFormData({...formData, video_url: e.target.value});
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="https://www.youtube.com/shorts/..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px',
                    color: theme.text
                  }}
                />
              </div>

              <div>
                <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                  GIF (optional)
                </label>
                {formData.gif_url ? (
                  <div>
                    <ExerciseDemo
                      gifUrl={formData.gif_url}
                      alt="Exercise preview"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '150px',
                        borderRadius: '8px',
                        border: `2px solid ${theme.surface3}`
                      }}
                    />
                    <div style={{ color: theme.success, fontSize: '12px', marginTop: '5px', marginBottom: '8px' }}>
                      ✓ GIF already set
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({...formData, gif_url: ''});
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.textMuted}`,
                        borderRadius: '8px',
                        color: theme.textMuted,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Change GIF
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="url"
                      value={formData.gif_url}
                      onChange={(e) => {
                        setFormData({...formData, gif_url: e.target.value});
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="https://example.com/exercise.gif"
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: theme.bg,
                        border: `1px solid ${theme.surface3}`,
                        borderRadius: '8px',
                        color: theme.text
                      }}
                    />
                    <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '5px' }}>
                      You can upload GIFs to services like Giphy or Imgur and paste the direct link here
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: theme.gold,
                    border: 'none',
                    borderRadius: '8px',
                    color: theme.bg,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {editingExercise ? 'Update Exercise' : 'Add Exercise'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.textMuted}`,
                    borderRadius: '8px',
                    color: theme.textMuted,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Exercises List */}
      <div style={{ display: 'grid', gap: '15px' }}>
        {getFilteredExercises().length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textMuted, padding: '40px' }}>
            No exercises found. Try a different search or filter.
          </div>
        ) : (
          getFilteredExercises().map(exercise => (
            <div
              key={exercise.id}
              id={`exercise-${exercise.id}`}
              style={{
                backgroundColor: theme.surface1,
                padding: '20px',
                borderRadius: '8px',
                border: `1px solid ${theme.surface3}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: theme.text, fontSize: '18px', marginBottom: '8px' }}>
                    {exercise.name}
                  </h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {exercise.movement_category && (
                      <span style={{
                        backgroundColor: theme.gold,
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.bg,
                        fontWeight: 700
                      }}>
                        {exercise.movement_category}
                      </span>
                    )}
                    {exercise.is_bodyweight && (
                      <span style={{
                        backgroundColor: theme.success,
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.bg,
                        fontWeight: 700
                      }}>
                        💪 Bodyweight {exercise.bodyweight_multiplier && exercise.bodyweight_multiplier !== 1.0 ? `(${Math.round(exercise.bodyweight_multiplier * 100)}%)` : ''}
                      </span>
                    )}
                    {exercise.is_assisted && (
                      <span style={{
                        backgroundColor: '#5AC8FA',
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.bg,
                        fontWeight: 700
                      }}>
                        🤝 Assisted
                      </span>
                    )}
                    {exercise.secondary_muscles && (
                      <span style={{
                        backgroundColor: theme.surface3,
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.textMuted
                      }}>
                        + {exercise.secondary_muscles}
                      </span>
                    )}
                    {exercise.equipment && (
                      <span style={{
                        backgroundColor: theme.surface3,
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.textMuted
                      }}>
                        {exercise.equipment}
                      </span>
                    )}
                    {exercise.difficulty_level && (
                      <span style={{
                        backgroundColor: theme.surface3,
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '12px',
                        color: theme.textMuted
                      }}>
                        {exercise.difficulty_level}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {exercise.video_url && (
                      <a
                        href={exercise.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: theme.bg,
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: theme.gold,
                          textDecoration: 'none'
                        }}
                      >
                        Video
                      </a>
                    )}
                    {exercise.gif_url && (
                      <a
                        href={exercise.gif_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: theme.bg,
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: theme.gold,
                          textDecoration: 'none'
                        }}
                      >
                        GIF
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                  <button
                    onClick={() => setShowSimilarFor(showSimilarFor === exercise.id ? null : exercise.id)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: showSimilarFor === exercise.id ? theme.gold : 'transparent',
                      border: `1px solid ${theme.gold}`,
                      borderRadius: '8px',
                      color: showSimilarFor === exercise.id ? theme.bg : theme.gold,
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Similar
                  </button>
                  {isSuperAdmin && <button
                    onClick={() => handleEdit(exercise)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.gold}`,
                      borderRadius: '8px',
                      color: theme.gold,
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Edit
                  </button>}
                  {isSuperAdmin && <button
                    onClick={() => handleDelete(exercise.id)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.danger}`,
                      borderRadius: '8px',
                      color: theme.danger,
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>}
                </div>
              </div>

              {/* Similar Exercises Panel */}
              {showSimilarFor === exercise.id && (
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  backgroundColor: theme.bg,
                  borderRadius: '8px',
                  border: `1px solid ${theme.surface3}`
                }}>
                  <h4 style={{ color: theme.gold, marginBottom: '10px', fontSize: '14px' }}>
                    Similar Exercises (Alternative Options)
                  </h4>
                  {getSimilarExercises(exercise).length === 0 ? (
                    <p style={{ color: theme.textMuted, fontSize: '13px' }}>
                      No similar exercises found. Add more exercises in the {exercise.movement_category} category to see alternatives.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {getSimilarExercises(exercise).map(similar => (
                        <div
                          key={similar.id}
                          style={{
                            backgroundColor: theme.surface1,
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${theme.surface3}`
                          }}
                        >
                          <div style={{ color: theme.text, fontSize: '13px', fontWeight: 700 }}>
                            {similar.name}
                          </div>
                          <div style={{ color: theme.textMuted, fontSize: '11px' }}>
                            {similar.equipment || 'No equipment'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Inline Edit Form - appears under this exercise when editing */}
              {editingExercise && editingExercise.id === exercise.id && showForm && (
                <div id={`exercise-form-${exercise.id}`} style={{
                  marginTop: '15px',
                  padding: '25px',
                  backgroundColor: theme.bg,
                  borderRadius: '8px',
                  border: `2px solid ${theme.gold}`
                }}>
                  <h3 style={{ color: theme.gold, marginBottom: '20px', fontSize: '18px' }}>
                    Edit Exercise
                  </h3>

                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gap: '15px' }}>
                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          Exercise Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => {
                            setFormData({...formData, name: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text,
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          Primary Movement Category *
                        </label>
                        <select
                          required
                          value={formData.movement_category}
                          onChange={(e) => {
                            setFormData({...formData, movement_category: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text,
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Select movement category</option>
                          {MOVEMENT_CATEGORIES.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '8px', fontSize: '12px' }}>
                          <strong style={{ color: theme.text, fontSize: '13px' }}>Muscles Involved in Exercise</strong>
                          <br />
                          Select all muscles activated during this exercise.
                        </label>

                        {/* Quick Auto-Suggest Buttons */}
                        {formData.movement_category && SECONDARY_MUSCLES[formData.movement_category] && (
                          <div style={{
                            marginBottom: '10px',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            borderRadius: '8px',
                            border: `1px solid ${theme.surface3}`
                          }}>
                            <div style={{ color: theme.gold, fontSize: '12px', marginBottom: '6px', fontWeight: 700 }}>
                              💡 Quick Apply:
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const suggestedMuscles = SECONDARY_MUSCLES[formData.movement_category].slice(0, 3).join(', ');
                                  setFormData({...formData, secondary_muscles: suggestedMuscles});
                                  setHasUnsavedChanges(true);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: theme.success,
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: theme.bg,
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: 700
                                }}
                              >
                                ✓ Top 3
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const allMuscles = SECONDARY_MUSCLES[formData.movement_category].join(', ');
                                  setFormData({...formData, secondary_muscles: allMuscles});
                                  setHasUnsavedChanges(true);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#5AC8FA',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: theme.bg,
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: 700
                                }}
                              >
                                ✓ All
                              </button>
                            </div>
                          </div>
                        )}

                        <MuscleSelector
                          selectedMuscles={formData.secondary_muscles}
                          onChange={(muscles) => {
                            setFormData({...formData, secondary_muscles: muscles});
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          Equipment
                        </label>
                        <input
                          type="text"
                          value={formData.equipment}
                          onChange={(e) => {
                            setFormData({...formData, equipment: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          placeholder="e.g., Barbell, Dumbbells"
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text,
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          Logging Type
                        </label>
                        <select
                          value={formData.exercise_type}
                          onChange={(e) => {
                            setFormData({...formData, exercise_type: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text,
                            marginBottom: '10px',
                          }}
                        >
                          <option value="resistance">Resistance (weight × reps)</option>
                          <option value="isometric">Isometric (hold in seconds)</option>
                          <option value="cardio">Cardio (duration + intensity)</option>
                          <option value="stretch">Stretch (duration + intensity)</option>
                        </select>
                        {(formData.exercise_type === 'cardio' || formData.exercise_type === 'stretch' || formData.exercise_type === 'isometric') && (
                          <div>
                            <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px', fontSize: '12px' }}>
                              Duration Unit
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {['minutes', 'seconds'].map(unit => (
                                <button
                                  key={unit}
                                  type="button"
                                  onClick={() => { setFormData({ ...formData, duration_unit: unit }); setHasUnsavedChanges(true); }}
                                  style={{
                                    flex: 1,
                                    padding: '8px',
                                    borderRadius: '8px',
                                    border: formData.duration_unit === unit ? `2px solid ${theme.gold}` : `1px solid ${theme.surface3}`,
                                    backgroundColor: formData.duration_unit === unit ? 'rgba(212,175,55,0.15)' : theme.surface1,
                                    color: formData.duration_unit === unit ? theme.gold : theme.textMuted,
                                    fontWeight: formData.duration_unit === unit ? '700' : '400',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                  }}
                                >
                                  {unit === 'minutes' ? 'Minutes' : 'Seconds'}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          Difficulty Level
                        </label>
                        <select
                          value={formData.difficulty_level}
                          onChange={(e) => {
                            setFormData({...formData, difficulty_level: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text
                          }}
                        >
                          <option>Beginner</option>
                          <option>Intermediate</option>
                          <option>Advanced</option>
                        </select>
                      </div>

                      {/* Exercise Type Toggles */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '15px',
                        padding: '15px',
                        backgroundColor: theme.surface1,
                        borderRadius: '8px',
                        border: `1px solid ${theme.surface3}`
                      }}>
                        <div>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            color: formData.is_bodyweight ? theme.gold : theme.textMuted,
                            fontWeight: formData.is_bodyweight ? 'bold' : 'normal',
                            fontSize: '14px'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.is_bodyweight}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  is_bodyweight: e.target.checked,
                                  is_assisted: e.target.checked ? false : formData.is_assisted,
                                  bodyweight_multiplier: e.target.checked ? formData.bodyweight_multiplier : 1.0
                                });
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: theme.gold
                              }}
                            />
                            Bodyweight Exercise
                          </label>
                        </div>

                        <div>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            color: formData.is_assisted ? theme.gold : theme.textMuted,
                            fontWeight: formData.is_assisted ? 'bold' : 'normal',
                            fontSize: '14px'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.is_assisted}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  is_assisted: e.target.checked,
                                  is_bodyweight: e.target.checked ? false : formData.is_bodyweight
                                });
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: theme.gold
                              }}
                            />
                            Assisted Exercise
                          </label>
                        </div>

                        <div>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            color: formData.is_unilateral ? theme.gold : theme.textMuted,
                            fontWeight: formData.is_unilateral ? 'bold' : 'normal',
                            fontSize: '14px'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.is_unilateral}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  is_unilateral: e.target.checked
                                });
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: theme.gold
                              }}
                            />
                            Unilateral (one side at a time)
                          </label>
                        </div>
                      </div>

                      {/* Bodyweight Multiplier - Only shown for bodyweight exercises */}
                      {formData.is_bodyweight && (
                        <div style={{
                          padding: '15px',
                          backgroundColor: theme.surface1,
                          borderRadius: '8px',
                          border: `2px solid ${theme.success}`
                        }}>
                          <label style={{ color: theme.success, display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                            ⚖️ Bodyweight Percentage
                          </label>
                          <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                            What % of bodyweight is moved?
                          </div>

                          {/* Quick Preset Buttons */}
                          <div style={{
                            marginBottom: '12px',
                            padding: '10px',
                            backgroundColor: theme.bg,
                            borderRadius: '8px'
                          }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                              {MULTIPLIER_PRESETS.slice(0, 9).map(preset => (
                                <button
                                  key={preset.value}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setFormData({...formData, bodyweight_multiplier: preset.value});
                                    setHasUnsavedChanges(true);
                                  }}
                                  style={{
                                    padding: '6px 8px',
                                    backgroundColor: formData.bodyweight_multiplier === preset.value ? theme.success : 'transparent',
                                    border: `1px solid ${formData.bodyweight_multiplier === preset.value ? theme.success : theme.surface3}`,
                                    borderRadius: '8px',
                                    color: formData.bodyweight_multiplier === preset.value ? theme.bg : theme.textMuted,
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: formData.bodyweight_multiplier === preset.value ? 'bold' : 'normal',
                                    textAlign: 'left'
                                  }}
                                >
                                  {preset.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Manual Input */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="number"
                              min="0.1"
                              max="1.0"
                              step="0.01"
                              value={formData.bodyweight_multiplier}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                if (value >= 0.1 && value <= 1.0) {
                                  setFormData({...formData, bodyweight_multiplier: value});
                                  setHasUnsavedChanges(true);
                                }
                              }}
                              style={{
                                flex: 1,
                                padding: '10px',
                                backgroundColor: theme.bg,
                                border: `2px solid ${theme.success}`,
                                borderRadius: '8px',
                                color: theme.text,
                                fontSize: '16px',
                                fontWeight: 700,
                                textAlign: 'center'
                              }}
                            />
                            <div style={{ color: theme.text, fontSize: '16px', fontWeight: 700 }}>
                              = {Math.round(formData.bodyweight_multiplier * 100)}%
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          YouTube Video URL
                        </label>
                        <input
                          type="url"
                          value={formData.video_url}
                          onChange={(e) => {
                            setFormData({...formData, video_url: e.target.value});
                            setHasUnsavedChanges(true);
                          }}
                          placeholder="https://www.youtube.com/shorts/..."
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: theme.surface1,
                            border: `1px solid ${theme.surface3}`,
                            borderRadius: '8px',
                            color: theme.text
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ color: theme.textMuted, display: 'block', marginBottom: '5px' }}>
                          GIF (optional)
                        </label>
                        {formData.gif_url ? (
                          <div>
                            <ExerciseDemo
                              gifUrl={formData.gif_url}
                              alt="Exercise preview"
                              style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                borderRadius: '8px',
                                border: `2px solid ${theme.surface3}`,
                                marginBottom: '10px'
                              }}
                            />
                            <div style={{ color: theme.success, fontSize: '12px', marginBottom: '8px' }}>
                              ✓ GIF preview
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({...formData, gif_url: ''});
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: 'transparent',
                                border: `1px solid ${theme.textMuted}`,
                                borderRadius: '8px',
                                color: theme.textMuted,
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Change GIF
                            </button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="url"
                              value={formData.gif_url}
                              onChange={(e) => {
                                setFormData({...formData, gif_url: e.target.value});
                                setHasUnsavedChanges(true);
                              }}
                              placeholder="https://example.com/exercise.gif"
                              style={{
                                width: '100%',
                                padding: '10px',
                                backgroundColor: theme.surface1,
                                border: `1px solid ${theme.surface3}`,
                                borderRadius: '8px',
                                color: theme.text
                              }}
                            />
                            <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '5px' }}>
                              Paste GIF URL to see preview
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button
                          type="submit"
                          style={{
                            flex: 1,
                            padding: '12px',
                            backgroundColor: theme.gold,
                            border: 'none',
                            borderRadius: '8px',
                            color: theme.bg,
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          Update Exercise
                        </button>
                        <button
                          type="button"
                          onClick={handleCancel}
                          style={{
                            flex: 1,
                            padding: '12px',
                            backgroundColor: 'transparent',
                            border: `2px solid ${theme.textMuted}`,
                            borderRadius: '8px',
                            color: theme.textMuted,
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, exerciseId: null })}
        onConfirm={handleConfirmDelete}
        title="Delete Exercise"
        message="Are you sure you want to delete this exercise? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default ExerciseLibraryManager;
